import { API_PROXY, PROFILE_API } from "@/constants/env";
import { ProfileUser } from "@/types/user";

export const getUserAddress = ({
  profileUser,
  addressType = "billingAddressId",
}: {
  profileUser?: ProfileUser;
  addressType?: "deliveryAddressId" | "billingAddressId";
}) => {
  if (!profileUser) return;
  const addressId = profileUser?.settings?.[addressType];
  const address = profileUser?.settings?.addresses?.find(
    (address) => address.id === addressId,
  );
  return address;
};

export const getUserName = ({
  user,
  billingAddress,
}: {
  user?: { email?: string; fullName?: string };
  billingAddress?: { firstName?: string; lastName?: string };
}) => {
  if (!user) return;
  if (billingAddress) {
    const { firstName = "", lastName = "" } = billingAddress;
    const nameFromAddress = [firstName, lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (nameFromAddress) {
      return nameFromAddress;
    }
  }
  return user.fullName || user.email || "";
};

export const getNameFromProfile = (
  profileUser?: ProfileUser,
  fallbackToEmail = true,
): string | undefined => {
  if (!profileUser) return;

  const { firstName, lastName } = profileUser.settings ?? {};
  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(" ").trim();
  }
  const address = getUserAddress({ profileUser });
  if (address) {
    const { firstName, lastName } = address;
    if (firstName || lastName) {
      return [firstName, lastName].filter(Boolean).join(" ").trim();
    }
  }
  if (fallbackToEmail) {
    return profileUser.email;
  }
  return undefined;
};

export type MaybeStoredAddress = {
  street: string;
  city: string;
  zip: string;
  country: string;
  firstName?: string;
  lastName?: string;
  co?: string;
  id?: string;
};

type UpdateUserSettingsProps = {
  accessToken: string;
  vendorId: string;
  userId: string;
  firstName?: string;
  lastName?: string;
  mobilePhone?: string;
  birthday?: string;
  preferredDeliveryCarrier?: string;
  businessDetails?: { companyName?: string; vatNr?: string };
  deliveryAddress?: MaybeStoredAddress;
  billingAddress?: MaybeStoredAddress;
  billingAddressIsSame?: boolean;
  disallowCommunication?: {
    email?: boolean;
    phone?: boolean;
    letter?: boolean;
  };
};

export const updateUserSettings = async ({
  accessToken,
  vendorId,
  userId,
  ...body
}: UpdateUserSettingsProps) => {
  const res = await fetch(
    `${PROFILE_API}/user/${userId}/vendors/${vendorId}/settings`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );
  return res.json();
};

type UpdateUserEmailProps = {
  accessToken: string;
  userId: string;
  email: string;
};

export const updateUserEmail = async ({
  accessToken,
  userId,
  email,
}: UpdateUserEmailProps) => {
  const res = await fetch(
    `${API_PROXY}/management/users/${userId}/change-login-identifier`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
      }),
    },
  );
  const data = await res.json();
  return data;
};

type DeleteUserProps = {
  accessToken: string;
  userId: string;
};

type SetPasswordProps = {
  accessToken: string;
  userId: string;
  password: string;
};

export const setPassword = async ({
  accessToken,
  userId,
  password,
}: SetPasswordProps) => {
  const res = await fetch(
    `${API_PROXY}/management/users/${userId}/set-password`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        password,
      }),
    },
  );

  if (!res.ok) {
    const response = await res.json().catch(() => ({
      message: "Failed to set password",
    }));
    const tooSmallIssue = response.error?.issues?.find(
      (issue: any) => issue.code === "too_small",
    );
    if (tooSmallIssue) {
      if (tooSmallIssue.minimum) {
        throw new Error(
          `Password must be at least ${tooSmallIssue.minimum} characters long`,
        );
      } else {
        throw new Error("Password is too weak");
      }
    } else {
      throw new Error(response.error?.message || "Failed to set password");
    }
  }

  const data = await res.json();
  return data;
};

export const deleteUser = async ({ accessToken, userId }: DeleteUserProps) => {
  const res = await fetch(`${API_PROXY}/management/users/${userId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({
      message: "Failed to delete user",
    }));
    throw new Error(error.message || "Failed to delete user");
  }

  return { success: true };
};
